import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import Pagination, { StatusBadge, KvRow, ReasonPrompt } from "./shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import api, { formatEUR, formatCrypto } from "../../lib/api";
import { toast } from "sonner";
import { Search, Flag, ChevronRight, Activity } from "lucide-react";
import { OrderStageList } from "../Stepper";

const STATUSES = ["awaiting_payment", "payment_received", "sourcing_liquidity", "sending_crypto", "on_chain_confirmed", "completed", "cancelled"];
const ASSETS = ["BTC", "ETH", "USDT", "USDC", "SOL", "XRP", "ADA", "DOGE", "MATIC", "DOT"];

export default function AdminOrders() {
  const [data, setData] = useState({ orders: [], total: 0, page: 1, page_size: 25 });
  const [filters, setFilters] = useState({ status: "", asset: "", payment_method: "", q: "" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: "25" });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    try {
      const r = await api.get(`/admin/orders?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [page, filters]);

  return (
    <AdminLayout title="Order Desk" subtitle="Every fiat-to-crypto order across the platform">
      <div className="xch-card p-5 mb-4" data-testid="orders-filters">
        <div className="grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by order ID or customer email"
              value={filters.q}
              onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }}
              className="rounded-xl pl-9"
              data-testid="orders-search-input"
            />
          </div>
          <FilterSelect placeholder="Status" value={filters.status} onChange={(v) => { setFilters({ ...filters, status: v }); setPage(1); }} options={STATUSES} testId="filter-status" />
          <FilterSelect placeholder="Asset" value={filters.asset} onChange={(v) => { setFilters({ ...filters, asset: v }); setPage(1); }} options={ASSETS} testId="filter-asset" />
          <FilterSelect placeholder="Method" value={filters.payment_method} onChange={(v) => { setFilters({ ...filters, payment_method: v }); setPage(1); }} options={["sepa", "card"]} testId="filter-method" />
        </div>
      </div>

      <div className="xch-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <Th>Order</Th><Th>Customer</Th><Th>Asset</Th><Th right>Amount</Th><Th>Method</Th><Th>Status</Th><Th>Partner</Th><Th>Created</Th><Th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-12 text-muted-foreground text-sm">Loading orders…</td></tr>
              )}
              {!loading && data.orders.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-muted-foreground text-sm">No orders match these filters.</td></tr>
              )}
              {data.orders.map((o) => (
                <tr key={o.id} className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setDrawerId(o.id)} data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{o.id.slice(0, 8)}…</td>
                  <td className="px-4 py-3 text-foreground truncate max-w-[200px]">{o.customer_email}</td>
                  <td className="px-4 py-3"><span className="font-display font-bold text-foreground">{o.asset}</span> <span className="text-xs text-muted-foreground">{o.network}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono">
                    <div className="text-foreground font-semibold">{formatEUR(o.fiat_amount)}</div>
                    <div className="text-[11px] text-muted-foreground">{formatCrypto(o.receive_amount, 4)} {o.asset}</div>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase font-semibold text-muted-foreground">{o.payment_method}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} />{o.flagged && <Flag className="w-3.5 h-3.5 inline-block ml-1.5 text-destructive" />}</td>
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{o.partner_id ? o.partner_id.slice(0, 6) + "…" : "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">
                    {new Date(o.created_at).toLocaleString("en-IE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-2 py-3 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-3">
          <Pagination page={page} pageSize={25} total={data.total} onPage={setPage} />
        </div>
      </div>

      <OrderDrawer orderId={drawerId} onClose={() => setDrawerId(null)} onRefresh={fetchData} />
    </AdminLayout>
  );
}

const Th = ({ children, right }) => (
  <th className={`px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);

const FilterSelect = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="rounded-xl" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All {placeholder.toLowerCase()}</SelectItem>
      {options.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}
    </SelectContent>
  </Select>
);

function OrderDrawer({ orderId, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState(null); // { action, label, danger }

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    api.get(`/admin/orders/${orderId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [orderId]);

  const refresh = () => orderId && api.get(`/admin/orders/${orderId}`).then((r) => setData(r.data));

  const runAction = async (reason) => {
    if (!prompt) return;
    try {
      if (prompt.action === "advance") {
        await api.post(`/admin/orders/${orderId}/advance`, { reason });
        toast.success("Stage advanced");
      } else if (prompt.action === "flag") {
        await api.post(`/admin/orders/${orderId}/flag`, { reason });
        toast.success("Order flagged");
      } else if (prompt.action === "freeze") {
        await api.post(`/admin/customers/${data.customer.id}/freeze`, { reason });
        toast.success("Customer frozen");
      } else if (prompt.action === "refund") {
        const r = await api.post(`/admin/orders/${orderId}/refund`, { reason });
        if (r.data?.status === "pending_second_approval") {
          toast.success("Refund proposed — awaiting second approver", {
            description: `Proposal ID: ${r.data.proposal_id?.slice(0, 8)}…. A different role-permitted staff member must approve before the refund row is created.`,
            duration: 8000,
          });
        } else {
          toast.success("Refund created");
        }
      }
      refresh();
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <Sheet open={!!orderId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[560px] sm:max-w-none overflow-y-auto" data-testid="order-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-xl">Order detail</SheetTitle>
        </SheetHeader>
        {loading || !data ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="bg-muted/40 rounded-2xl p-3">
              <div className="font-mono text-xs text-muted-foreground break-all">{data.order.id}</div>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={data.order.status} />
                {data.order.flagged && <span className="text-[10px] font-semibold text-destructive uppercase tracking-wider">Flagged</span>}
                {data.customer?.is_frozen && <span className="text-[10px] font-semibold text-destructive uppercase tracking-wider">Customer Frozen</span>}
              </div>
            </div>

            <Section title="Customer">
              <KvRow label="Email" value={data.customer?.email} />
              <KvRow label="Name" value={data.customer?.name} />
              <KvRow label="Country" value={data.customer?.country} />
              <KvRow label="KYC tier" value={`Tier ${data.customer?.kyc_tier || 0} · ${data.customer?.kyc_status}`} />
            </Section>

            <Section title="Order">
              <KvRow label="Asset" value={`${data.order.asset} · ${data.order.network}`} />
              <KvRow label="Fiat amount" value={formatEUR(data.order.fiat_amount)} />
              <KvRow label="Crypto amount" value={`${formatCrypto(data.order.receive_amount)} ${data.order.asset}`} />
              <KvRow label="Quoted rate" value={formatEUR(data.order.quoted_rate)} />
              <KvRow label="Method" value={data.order.payment_method?.toUpperCase()} />
              <KvRow label="Partner" value={data.order.partner_id || "—"} mono />
              <KvRow label="Created" value={new Date(data.order.created_at).toLocaleString("en-IE")} />
            </Section>

            <Section title="Wallet">
              <KvRow label="Address" value={data.order.wallet_address} mono />
              <KvRow label="On-chain tx" value={data.order.on_chain_tx_hash || "—"} mono />
            </Section>

            {data.order.payment_method === "sepa" && (
              <Section title="SEPA payment">
                <KvRow label="IBAN" value={data.order.iban} mono />
                <KvRow label="BIC" value={data.order.bic} mono />
                <KvRow label="Reference" value={data.order.reference} mono />
                {/* Iter46 — XGate Payment URL paste-back. Ops creates the
                    invoice in app.xgateway.tech, copies the URL here, customer
                    gets an email + sees a "Continue to payment" CTA. */}
                {data.order.xgate_payment_url ? (
                  <KvRow label="XGate Payment URL"
                          value={
                            <a href={data.order.xgate_payment_url} target="_blank" rel="noopener noreferrer"
                                className="text-primary underline break-all"
                                data-testid="admin-xgate-url-link">
                              {data.order.xgate_payment_url.slice(0, 48)}…
                            </a>
                          } />
                ) : (
                  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2"
                        data-testid="admin-xgate-paste-back">
                    <div className="text-[12px] text-amber-900 font-bold">
                      No XGate invoice yet
                    </div>
                    <div className="text-[11px] text-amber-900/85">
                      1. Create the invoice in <a href="https://app.xgateway.tech/invoices" target="_blank" rel="noopener noreferrer" className="underline">app.xgateway.tech/invoices</a>.
                      2. Paste the Payment URL below — customer is auto-emailed.
                    </div>
                    <div className="flex gap-2">
                      <input type="url"
                              placeholder="https://app.xgateway.tech/pay/..."
                              className="flex-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[12px] font-mono"
                              value={data.xgatePasteUrl || ""}
                              onChange={(e) => setData({ ...data, xgatePasteUrl: e.target.value })}
                              data-testid="admin-xgate-paste-input" />
                      <Button size="sm" disabled={!data.xgatePasteUrl}
                               className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[12px]"
                               data-testid="admin-xgate-paste-submit"
                               onClick={async () => {
                                 try {
                                   await api.post(`/admin/orders/${data.order.id}/xgate-payment-url`,
                                                   { xgate_payment_url: data.xgatePasteUrl });
                                   toast.success("Payment URL set — customer notified");
                                   refresh();
                                 } catch (e) {
                                   toast.error(e?.response?.data?.detail || "Save failed");
                                 }
                               }}>
                        Save & notify
                      </Button>
                    </div>
                  </div>
                )}
              </Section>
            )}
            {data.order.payment_method === "card" && (
              <Section title="Stripe payment">
                <KvRow label="Session ID" value={data.order.checkout_session_id} mono />
              </Section>
            )}

            <Section title="Timeline">
              <OrderStageList stages={data.order.stages || []} />
            </Section>

            {data.webhook_logs?.length > 0 && (
              <Section title={`Webhook delivery (${data.webhook_logs.length})`}>
                {data.webhook_logs.map((w) => (
                  <div key={w.id} className="text-xs space-y-0.5 py-1.5 border-b border-slate-100 last:border-0">
                    <div className="flex items-center justify-between">
                      <StatusBadge status={w.status} />
                      <span className="text-muted-foreground tabular-nums">{new Date(w.last_attempt_at).toLocaleString("en-IE")}</span>
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground truncate">{w.url}</div>
                    <div className="text-[11px] text-muted-foreground">Attempts: {w.attempts} · Last status: {w.last_status || "—"}</div>
                  </div>
                ))}
              </Section>
            )}

            {data.flags?.length > 0 && (
              <Section title={`Compliance flags (${data.flags.length})`}>
                {data.flags.map((f) => (
                  <div key={f.id} className="text-xs flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                    <div>
                      <div className="font-semibold text-foreground">{f.reason}</div>
                      <div className="text-muted-foreground">{new Date(f.created_at).toLocaleString("en-IE")}</div>
                    </div>
                    <StatusBadge status={f.status} />
                  </div>
                ))}
              </Section>
            )}

            <Section title="Audit trail">
              {data.audit.length === 0 ? (
                <div className="text-xs text-muted-foreground">No actions yet.</div>
              ) : data.audit.map((a) => (
                <div key={a.id} className="text-xs py-1.5 border-b border-slate-100 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-foreground">{a.action}</span>
                    <span className="text-muted-foreground tabular-nums">{new Date(a.timestamp).toLocaleString("en-IE")}</span>
                  </div>
                  <div className="text-muted-foreground">{a.actor_email} {a.reason ? `· ${a.reason}` : ""}</div>
                </div>
              ))}
            </Section>

            <div className="flex flex-wrap gap-2 sticky bottom-0 bg-white pt-3 pb-2 border-t border-border">
              <Button onClick={() => setPrompt({ action: "advance", label: "Advance stage" })} className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1" size="sm" data-testid="order-advance-btn" disabled={data.order.status === "completed" || data.order.status === "cancelled"}>
                <Activity className="w-3.5 h-3.5" /> Advance stage
              </Button>
              <Button onClick={() => setPrompt({ action: "flag", label: "Flag for review" })} variant="outline" size="sm" className="rounded-xl gap-1" data-testid="order-flag-btn">
                <Flag className="w-3.5 h-3.5" /> Flag
              </Button>
              <Button onClick={() => setPrompt({ action: "refund", label: "Refund order", danger: true })} variant="outline" size="sm" className="rounded-xl gap-1 text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="order-refund-btn">
                Refund
              </Button>
              <Button onClick={() => setPrompt({ action: "freeze", label: "Freeze customer", danger: true })} variant="outline" size="sm" className="rounded-xl text-destructive border-destructive/30 hover:bg-destructive/5" data-testid="order-freeze-btn">
                Freeze customer
              </Button>
            </div>
          </div>
        )}
        <ReasonPrompt
          open={!!prompt}
          onOpenChange={() => setPrompt(null)}
          title={prompt?.label || ""}
          description="This action is logged to the audit trail."
          onConfirm={runAction}
          confirmLabel={prompt?.label || "Confirm"}
          danger={prompt?.danger}
        />
      </SheetContent>
    </Sheet>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-1">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{title}</div>
    <div className="bg-white border border-border rounded-2xl px-3">{children}</div>
  </div>
);
